import { useEffect, useState } from 'react'
import toast, { Toaster } from 'react-hot-toast'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ModuleWindow } from '../../components/moduleWindow/ModuleWindow'
import { translations } from '../../configs/translations'
import { RaceTypes } from '../../enums/RaceTypes'
import { UserProfile } from '../../models/UserProfile'
import { ConsumableInfo, ConsumableService, ConsumableType } from '../../services/ConsumableService'
import { useStore } from '../../store/store'
import { formatTextWithParams } from '../../utils/formatTextWithParams'
import { Header } from '../main/header/Header'
import './ConsumableShop.css'
import { ConsumableItem } from './consumableItem/ConsumableItem'

export const ConsumableMaximums = {
    fuel: 100,
    oil: 30,
    nitro: 100,
    tyres: 30
}

export const ConsumableShop = () => {

    const language = useStore(state => state.language) as keyof typeof translations
    const t = translations[language]

    const [userProfile, setUserProfile] = useState<UserProfile | null>(JSON.parse(localStorage.getItem('profile') || 'null'))

    const [searchParams] = useSearchParams()
    const vehicleId = Number(searchParams.get("vehicleId"))

    const [fuelAmount, setFuelAmount] = useState(20)
    const [oilAmount, setOilAmount] = useState(10)
    const [nitroAmount, setNitroAmount] = useState(10)
    const [tyreAmount, setTyreAmount] = useState(3)

    const [fuelMax, setFuelMax] = useState(ConsumableMaximums.fuel)
    const [oilMax, setOilMax] = useState(ConsumableMaximums.oil)
    const [nitroMax, setNitroMax] = useState(ConsumableMaximums.nitro)
    const [tyreMax, setTyreMax] = useState(ConsumableMaximums.tyres)

    const [consumableInfo, setConsumableInfo] = useState<ConsumableInfo | null>(null)

    const [modalData, setShowModalData] = useState<Record<string, any> | null>()

    const updateMaximums = (consumableInfo: ConsumableInfo) => {
        setFuelMax(ConsumableMaximums.fuel - consumableInfo.remaining.FUEL)
        setOilMax(ConsumableMaximums.oil - consumableInfo.remaining.OIL)
        setNitroMax(ConsumableMaximums.nitro - consumableInfo.remaining.NITROUS)
        setTyreMax(ConsumableMaximums.tyres - consumableInfo.remaining.TYRE)
    }

    const fetchInfo = async () => {
        await ConsumableService.fetchConsumableInfo(vehicleId)
            .then((response) => {
                setConsumableInfo(response)
                updateMaximums(response)
            })
            .catch((error) => {
                toast.error(
                    error && error.response && error.response.data && t[error.response.data as keyof typeof t]
                        ? t[error.response.data as keyof typeof t]
                        : t.error_unknown,
                    { className: 'notification-error' }
                )
            })
    }

    useEffect(() => {
        if (!vehicleId) return
        if (!consumableInfo) fetchInfo()
    }, [vehicleId])

    const buySound = new Audio('./sounds/buy.mp3')
    buySound.volume = 0.1

    const buyItem = async (type: ConsumableType, amount: number) => {
        await ConsumableService.buyConsumable(vehicleId, type, amount)
            .then(() => {
                buySound.play()
                fetchInfo()
                setShowModalData(null)
                setUserProfile(null)
            })
            .catch((error) => {
                setShowModalData(null)
                toast.error(
                    error && error.response && error.response.data && t[error.response.data as keyof typeof t]
                        ? t[error.response.data as keyof typeof t]
                        : t.error_unknown,
                    { className: 'notification-error' }
                )
            })
    }

    const buyFuel = () => {
        if (!consumableInfo) return
        const price = Math.ceil(fuelAmount * consumableInfo.priceList.FUEL)
        const itemAmountWithSymbol = Math.round(fuelAmount) + ' ' + t.liters_short
        setShowModalData({
            title: t.buy_consumable_title,
            text: formatTextWithParams(t.buy_consumable_desc,
                [t.fuel, itemAmountWithSymbol, price]
            ),
            onYes: () => buyItem('FUEL', fuelAmount),
            onNo: () => setShowModalData(null),
        })
    }
    const buyOil = () => {
        if (!consumableInfo) return
        const price = Math.ceil(oilAmount * consumableInfo.priceList.OIL)
        const itemAmountWithSymbol = Math.round(oilAmount) + ' ' + t.liters_short
        setShowModalData({
            title: t.buy_consumable_title,
            text: formatTextWithParams(t.buy_consumable_desc,
                [t.oil, itemAmountWithSymbol, price]
            ),
            onYes: () => buyItem('OIL', oilAmount),
            onNo: () => setShowModalData(null),
        })
    }
    const buyTyre = () => {
        if (!consumableInfo) return
        const price = Math.ceil(tyreAmount * consumableInfo.priceList.TYRE)
        const itemAmountWithSymbol = Math.round(tyreAmount) + ' ' + t.amounts_short
        setShowModalData({
            title: t.buy_consumable_title,
            text: formatTextWithParams(t.buy_consumable_desc,
                [t.tyre, itemAmountWithSymbol, price]
            ),
            onYes: () => buyItem('TYRE', tyreAmount),
            onNo: () => setShowModalData(null),
        })
    }
    const buyNitro = () => {
        if (!consumableInfo) return
        const price = Math.ceil(nitroAmount * consumableInfo.priceList.NITROUS)
        const itemAmountWithSymbol = Math.round(nitroAmount) + ' ' + t.amounts_short
        setShowModalData({
            title: t.buy_consumable_title,
            text: formatTextWithParams(t.buy_consumable_desc,
                [t.nitro, itemAmountWithSymbol, price]
            ),
            onYes: () => buyItem('NITROUS', nitroAmount),
            onNo: () => setShowModalData(null),
        })
    }




    const navigate = useNavigate()
    useEffect(() => {
        fetchInfo()
        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                navigate('/main')
            }
        })
        return () => {
            document.removeEventListener('keydown', (event) => {
                if (event.key === 'Escape') {
                    navigate('/main')
                }
            })
        }
    }, [])

    const audio = new Audio('./sounds/menu_sound_1.wav')
    useEffect(() => {
        audio.volume = 0.05
        audio.play()
    }, [fuelAmount, oilAmount, nitroAmount, tyreAmount])

    return (
        <>
            {modalData &&
                <ModuleWindow
                    title={modalData.title}
                    text={modalData.text}
                    onYes={modalData.onYes}
                    onNo={modalData.onNo}
                />
            }
            {consumableInfo && <div className="consumable-shop">
                <Header
                    selectedMode={RaceTypes.RANKED}
                    selectedVehicle={null}
                    profile={userProfile}
                    setProfile={setUserProfile}
                    title={t.consumables}
                />

                <div className='consumable-items-wrapper'>
                    <div className='consumable-items-list'>
                        <ConsumableItem
                            type="fuel"
                            text={t.fuel}
                            amount={fuelAmount}
                            setAmount={setFuelAmount}
                            pricePerItem={consumableInfo.priceList.FUEL}
                            onBuyClicked={buyFuel}
                            availableAmount={consumableInfo.remaining.FUEL}
                            max={fuelMax}
                            min={1}
                            step={1}
                            measureText={t.liters_short}
                        />
                        <ConsumableItem
                            type="tyre"
                            text={t.tyre}
                            amount={tyreAmount}
                            setAmount={setTyreAmount}
                            pricePerItem={consumableInfo.priceList.TYRE}
                            onBuyClicked={buyTyre}
                            availableAmount={consumableInfo.remaining.TYRE}
                            max={tyreMax}
                            min={1}
                            step={1}
                            measureText={t.amounts_short}
                        />
                        <ConsumableItem
                            type="oil"
                            text={t.oil}
                            amount={oilAmount}
                            setAmount={setOilAmount}
                            pricePerItem={consumableInfo.priceList.OIL}
                            onBuyClicked={buyOil}
                            availableAmount={consumableInfo.remaining.OIL}
                            max={oilMax}
                            min={1}
                            step={1}
                            measureText={t.liters_short}
                        />
                        <ConsumableItem
                            type="nitro"
                            text={t.nitro}
                            amount={nitroAmount}
                            setAmount={setNitroAmount}
                            pricePerItem={consumableInfo.priceList.NITROUS}
                            onBuyClicked={buyNitro}
                            availableAmount={consumableInfo.remaining.NITROUS}
                            max={nitroMax}
                            min={1}
                            step={1}
                            measureText={"%"}
                        />
                    </div>
                </div>
            </div>}
            <Toaster position="bottom-center" />
        </>
    )
}